import { useState, type FormEvent } from "react";
import type { SessionUser } from "@rma-orbit/shared";
import { api } from "../api/client.js";
import { ErrorNotice, Notice } from "../lib/ui.js";
import { NetworkCanvas } from "../lib/NetworkCanvas.js";

/**
 * 로그인. 가입은 없다 — 대표가 [계정] 화면에서 계정을 만든다 (harness/SECURITY.md §1).
 *
 * 뒤에는 별자리 캔버스가 천천히 움직이고, 카드는 떠오르며 들어온다. 로그인이 되면
 * 카드가 위로 사라지고(`leaving`) 그 다음에 앱이 켜진다 — 화면이 툭 바뀌지 않게.
 */
export function Login({
  onLoggedIn,
  expired = false,
}: {
  onLoggedIn: (user: SessionUser) => void;
  /** 세션이 끊겨서 돌아온 것인가 — 처음 여는 것과 구분해서 말해 준다 */
  expired?: boolean;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [leaving, setLeaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const user = await api.login(email, password);
      setLeaving(true);
      // 카드가 사라지는 시간만큼 기다렸다가 앱을 켠다 (styles.css .login-card.leaving)
      window.setTimeout(() => onLoggedIn(user), 420);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  return (
    <div className={`login-wrap ${leaving ? "leaving" : ""}`}>
      <NetworkCanvas />
      <form className={`login-card ${leaving ? "leaving" : ""}`} onSubmit={submit}>
        <div className="login-brand">
          <span className="brand-mark big" aria-hidden="true" />
          <div>
            <h1>
              RMA <span>ORBIT</span>
            </h1>
            <p>Triz MS 방화벽 RMA 관제</p>
          </div>
        </div>

        {expired && (
          <Notice kind="warn">
            세션이 끝나 로그아웃되었습니다. 다시 로그인해 주세요 — 하시던 작업은 저장돼 있습니다.
          </Notice>
        )}
        <ErrorNotice error={error} />

        <label className="field" style={{ marginBottom: 12 }}>
          이메일
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@trizms.co.kr"
            required
            autoFocus
          />
        </label>
        <label className="field" style={{ marginBottom: 20 }}>
          비밀번호
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
          />
        </label>

        <button className="primary login-btn" disabled={busy}>
          {busy ? "확인 중…" : "로그인"}
        </button>
        <p className="login-foot">원 시리얼과 교체 시리얼의 연결이 끊기지 않게.</p>
      </form>
    </div>
  );
}
